import { defineComponent, useSlots, ref, computed, watch, nextTick, onMounted, onBeforeUnmount, onUnmounted, createElementBlock, openBlock, createElementVNode, withDirectives, toDisplayString, createBlock, createCommentVNode, renderSlot, unref, withCtx, vShow, createTextVNode, createVNode } from 'vue';
import { useMediaQuery, onClickOutside } from './basler-components.es419.js';
import _sfc_main$1 from './basler-components.es72.js';
import _sfc_main$2 from './basler-components.es81.js';
/* empty css                       */
import { useSlotContent } from './basler-components.es425.js';
import { animate } from './basler-components.es446.js';
import { useDebounceFn } from './basler-components.es436.js';

const _hoisted_1 = ["aria-label", "aria-expanded"];
const _hoisted_2 = { class: "action-bar__expanded-inner" };
const _hoisted_3 = { class: "action-bar__header" };
const _hoisted_4 = {
  key: 0,
  class: "action-bar__header-title"
};
const _hoisted_5 = { class: "visually-hidden" };
const BORDER_WIDTH = 3;
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ActionBar",
  props: {
    isExpanded: { type: Boolean, default: false },
    closeButtonLabel: { default: "Close" },
    expandedHeadline: { default: "" },
    showCheckmark: { type: Boolean, default: false },
    ariaLabel: { default: "Product action bar" }
  },
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const animate$1 = (target, keyframes, options) => {
      return animate(target, keyframes, options);
    };
    const ANIMATION_DURATION = {
      FAST: 0.4,
      FAST_MOBILE: 0.2,
      // Noch schneller auf Mobile
      CONTENT_FADE: 0.4,
      CONTENT_FADE_MOBILE: 0.2,
      MOBILE_FADE: 0.2
      // Schnelleres Einblenden für Mobile-Toggle
    };
    const ANIMATION_EASING = {
      IN_OUT: "ease-in-out",
      IN: "ease-in",
      OUT: "ease-out"
    };
    const getTotalHeight = (element) => {
      return element.offsetHeight + BORDER_WIDTH;
    };
    const props = __props;
    const slots = useSlots();
    const { hasSlotContent } = useSlotContent();
    const emit = __emit;
    const actionBarRef = ref(null);
    const initialRef = ref(null);
    const mobileRef = ref(null);
    const expandedRef = ref(null);
    const expandedContentRef = ref(null);
    const closeButtonRef = ref(null);
    const ariaLiveRef = ref(null);
    const resizeObserverRef = ref(null);
    const internalExpanded = ref(props.isExpanded);
    const internalMobileView = ref(false);
    const initialHeight = ref(0);
    const mobileHeight = ref(0);
    const ariaLiveMessage = ref("");
    const isMobileBreakpoint = useMediaQuery("(max-width: 991px)");
    const hasMobileSlot = computed(() => hasSlotContent(slots["mobile-content"]));
    const getAnimationDuration = (duration, mobileDuration) => {
      return isMobileBreakpoint.value && mobileDuration !== void 0 ? mobileDuration : duration;
    };
    watch(
      () => props.isExpanded,
      async (newVal) => {
        if (newVal !== internalExpanded.value) {
          if (newVal) {
            internalExpanded.value = newVal;
            await expandActionBar();
          } else {
            await collapseActionBar();
            internalExpanded.value = newVal;
          }
        }
      }
    );
    const updateActionBarHeight = () => {
      if (!actionBarRef.value) return;
      const height = actionBarRef.value.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty("--action-bar-height", `${height}px`);
      }
    };
    const updateBodyPadding = () => {
      if (internalExpanded.value) return;
      const currentRef = internalMobileView.value && isMobileBreakpoint.value ? mobileRef.value : initialRef.value;
      if (!currentRef) return;
      const height = currentRef.offsetHeight;
      if (height > 0) {
        document.body.style.paddingBottom = `${height}px`;
      }
      updateActionBarHeight();
    };
    const handleViewportChange = useDebounceFn((isMobile) => {
      if (internalExpanded.value) return;
      if (isMobile) {
        if (hasMobileSlot.value) {
          internalMobileView.value = true;
          if (mobileRef.value) {
            mobileRef.value.style.opacity = "1";
          }
          if (initialRef.value) {
            initialRef.value.style.opacity = "0";
          }
        } else {
          internalMobileView.value = false;
          if (initialRef.value) {
            initialRef.value.style.opacity = "1";
          }
        }
      } else {
        internalMobileView.value = false;
        if (initialRef.value) {
          initialRef.value.style.opacity = "1";
        }
        if (mobileRef.value) {
          mobileRef.value.style.opacity = "0";
        }
      }
      nextTick(() => {
        updateBodyPadding();
      });
    }, 150);
    watch(isMobileBreakpoint, (isMobile) => {
      handleViewportChange(isMobile);
    });
    const expand = async () => {
      internalExpanded.value = true;
      await expandActionBar();
    };
    const collapse = async () => {
      internalExpanded.value = false;
      await collapseActionBar();
    };
    const handleClose = async () => {
      if (isMobileBreakpoint.value && hasMobileSlot.value) {
        internalMobileView.value = true;
      } else {
        internalMobileView.value = false;
      }
      await collapse();
      emit("close");
    };
    const expandActionBar = async () => {
      if (!expandedRef.value || !expandedContentRef.value) return;
      const currentRef = internalMobileView.value ? mobileRef.value : initialRef.value;
      if (!currentRef) return;
      document.body.style.overflow = "hidden";
      document.body.style.paddingBottom = "";
      const currentHeight = currentRef.offsetHeight;
      if (expandedRef.value) {
        expandedRef.value.style.height = "auto";
        expandedRef.value.style.maxHeight = "80dvh";
        expandedRef.value.style.visibility = "hidden";
        expandedRef.value.style.overflow = "visible";
      }
      await nextTick();
      const viewportHeight = window.innerHeight;
      const maxExpandedHeight = Math.min(viewportHeight * 0.8, 1e3);
      const expandedHeight = expandedRef.value ? Math.min(expandedRef.value.scrollHeight, maxExpandedHeight) : maxExpandedHeight;
      const totalExpandedHeight = expandedHeight + BORDER_WIDTH;
      if (expandedRef.value) {
        expandedRef.value.style.height = `${currentHeight}px`;
        expandedRef.value.style.visibility = "visible";
        expandedRef.value.style.overflow = "hidden";
      }
      expandedContentRef.value.style.opacity = "0";
      if (actionBarRef.value) {
        actionBarRef.value.style.height = `${getTotalHeight(currentRef)}px`;
        const fadeDuration = getAnimationDuration(ANIMATION_DURATION.CONTENT_FADE, ANIMATION_DURATION.CONTENT_FADE_MOBILE);
        const heightDuration = getAnimationDuration(ANIMATION_DURATION.FAST, ANIMATION_DURATION.FAST_MOBILE);
        await Promise.all([
          // Aktuelle Ansicht ausblenden
          animate$1(currentRef, { opacity: 0 }, { duration: fadeDuration, easing: ANIMATION_EASING.OUT }),
          // ActionBar-Höhe animieren
          animate$1(
            actionBarRef.value,
            { height: `${totalExpandedHeight}px` },
            { duration: heightDuration, easing: ANIMATION_EASING.IN_OUT }
          ),
          // Erweiterten Inhalt einblenden
          (async () => {
            if (expandedRef.value) {
              expandedRef.value.style.height = `${expandedHeight}px`;
            }
            if (expandedContentRef.value) {
              return animate$1(
                expandedContentRef.value,
                { opacity: 1 },
                { duration: fadeDuration, easing: ANIMATION_EASING.IN }
              );
            }
          })()
        ]);
      }
      if (actionBarRef.value) {
        actionBarRef.value.style.height = "auto";
      }
      if (expandedRef.value) {
        expandedRef.value.style.height = "auto";
      }
      if (initialRef.value) {
        initialRef.value.style.opacity = "0";
        initialRef.value.style.visibility = "hidden";
        initialRef.value.style.display = "none";
      }
      if (mobileRef.value) {
        mobileRef.value.style.opacity = "0";
        mobileRef.value.style.visibility = "hidden";
        mobileRef.value.style.display = "none";
      }
      await nextTick();
      if (closeButtonRef.value && closeButtonRef.value.$el) {
        closeButtonRef.value.$el.focus();
      }
      updateActionBarHeight();
    };
    const collapseActionBar = async () => {
      if (!expandedRef.value || !expandedContentRef.value) return;
      const targetRef = isMobileBreakpoint.value && hasMobileSlot.value ? mobileRef.value : initialRef.value;
      if (!targetRef) return;
      document.body.style.overflow = "";
      await nextTick();
      updateBodyPadding();
      const currentHeight = expandedRef.value.offsetHeight;
      const actionBarHeight = actionBarRef.value ? actionBarRef.value.offsetHeight : 0;
      if (actionBarRef.value) {
        actionBarRef.value.style.height = `${actionBarHeight}px`;
      }
      targetRef.style.visibility = "hidden";
      targetRef.style.opacity = "0";
      targetRef.style.display = "flex";
      await nextTick();
      const totalTargetHeight = getTotalHeight(targetRef);
      targetRef.style.display = "";
      targetRef.style.visibility = "visible";
      expandedRef.value.style.height = "0";
      if (actionBarRef.value) {
        actionBarRef.value.style.height = `${currentHeight + BORDER_WIDTH}px`;
        const fadeDuration = getAnimationDuration(ANIMATION_DURATION.CONTENT_FADE, ANIMATION_DURATION.CONTENT_FADE_MOBILE);
        const heightDuration = getAnimationDuration(ANIMATION_DURATION.FAST, ANIMATION_DURATION.FAST_MOBILE);
        await Promise.all([
          // Erweiterten Inhalt ausblenden
          animate$1(expandedContentRef.value, { opacity: 0 }, { duration: fadeDuration, easing: ANIMATION_EASING.OUT }),
          // ActionBar-Höhe zur Ziel-Ansicht animieren
          animate$1(
            actionBarRef.value,
            { height: `${totalTargetHeight}px` },
            { duration: heightDuration, easing: ANIMATION_EASING.IN_OUT }
          ),
          // Ziel-Ansicht einblenden
          animate$1(targetRef, { opacity: 1 }, { duration: fadeDuration, easing: ANIMATION_EASING.IN })
        ]);
      }
      if (actionBarRef.value) {
        actionBarRef.value.style.height = "auto";
      }
      if (targetRef) {
        targetRef.style.opacity = "";
        targetRef.style.visibility = "";
        targetRef.style.display = "";
      }
      const nonTargetRef = targetRef === mobileRef.value ? initialRef.value : mobileRef.value;
      if (nonTargetRef) {
        nonTargetRef.style.opacity = "0";
        nonTargetRef.style.visibility = "hidden";
        nonTargetRef.style.display = "none";
      }
      await nextTick();
      updateBodyPadding();
      updateActionBarHeight();
    };
    onClickOutside(actionBarRef, () => {
      if (internalExpanded.value) {
        handleClose();
      }
    });
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && internalExpanded.value) {
        handleClose();
      }
    };
    onMounted(async () => {
      document.addEventListener("keydown", handleKeyDown);
      await nextTick();
      if (actionBarRef.value && typeof ResizeObserver !== "undefined") {
        resizeObserverRef.value = new ResizeObserver(() => {
          updateActionBarHeight();
        });
        resizeObserverRef.value.observe(actionBarRef.value);
      }
      if (isMobileBreakpoint.value && hasMobileSlot.value) {
        internalMobileView.value = true;
      } else {
        internalMobileView.value = false;
      }
      const visibleRef = internalExpanded.value ? expandedRef.value : internalMobileView.value && isMobileBreakpoint.value ? mobileRef.value : initialRef.value;
      if (initialRef.value) {
        initialHeight.value = initialRef.value.offsetHeight;
        initialRef.value.style.opacity = internalExpanded.value ? "0" : internalMobileView.value && isMobileBreakpoint.value ? "0" : "1";
      }
      if (mobileRef.value) {
        mobileHeight.value = mobileRef.value.offsetHeight;
        mobileRef.value.style.opacity = internalExpanded.value ? "0" : internalMobileView.value && isMobileBreakpoint.value ? "1" : "0";
      }
      if (expandedContentRef.value) {
        expandedContentRef.value.style.opacity = internalExpanded.value ? "1" : "0";
      }
      if (internalExpanded.value && expandedRef.value) {
        expandedRef.value.style.height = "1000px";
        expandedRef.value.style.maxHeight = "80dvh";
      }
      if (actionBarRef.value && visibleRef) {
        actionBarRef.value.style.transform = "translate3d(0, 100%, 0)";
        await animate$1(actionBarRef.value, { transform: "translate3d(0, 0, 0)" }, { duration: 0.3, easing: "ease-out" });
        await nextTick();
        updateBodyPadding();
        updateActionBarHeight();
      }
    });
    onBeforeUnmount(async () => {
      if (internalExpanded.value) {
        document.body.style.overflow = "";
      }
      document.body.style.paddingBottom = "";
      const visibleRef = internalExpanded.value ? expandedContentRef.value : internalMobileView.value && isMobileBreakpoint.value ? mobileRef.value : initialRef.value;
      if (visibleRef) {
        await animate$1(visibleRef, { opacity: 0 }, { duration: 0.15, easing: "ease-out" });
      }
      if (actionBarRef.value) {
        await animate$1(actionBarRef.value, { transform: "translate3d(0, 100%, 0)" }, { duration: 0.25, easing: "ease-in" });
      }
    });
    onUnmounted(() => {
      document.removeEventListener("keydown", handleKeyDown);
      if (resizeObserverRef.value) {
        resizeObserverRef.value.disconnect();
        resizeObserverRef.value = null;
      }
      document.documentElement.style.removeProperty("--action-bar-height");
    });
    const announce = (message) => {
      ariaLiveMessage.value = message;
      setTimeout(() => {
        ariaLiveMessage.value = "";
      }, 1e3);
    };
    const scrollToTop = () => {
      if (expandedContentRef.value) {
        expandedContentRef.value.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    const toggleMobile = async () => {
      if (!isMobileBreakpoint.value) return;
      if (internalExpanded.value) return;
      const currentRef = internalMobileView.value ? mobileRef.value : initialRef.value;
      const nextRef = internalMobileView.value ? initialRef.value : mobileRef.value;
      if (!currentRef || !nextRef || !actionBarRef.value) return;
      const totalCurrentHeight = getTotalHeight(currentRef);
      nextRef.style.visibility = "hidden";
      nextRef.style.display = "flex";
      await nextTick();
      const totalNextHeight = getTotalHeight(nextRef);
      nextRef.style.display = "";
      nextRef.style.visibility = "visible";
      nextRef.style.opacity = "0";
      actionBarRef.value.style.height = `${totalCurrentHeight}px`;
      internalMobileView.value = !internalMobileView.value;
      const heightDuration = getAnimationDuration(ANIMATION_DURATION.FAST, ANIMATION_DURATION.FAST_MOBILE);
      await Promise.all([
        // Höhenanimation
        animate$1(
          actionBarRef.value,
          { height: `${totalNextHeight}px` },
          { duration: heightDuration, easing: ANIMATION_EASING.IN_OUT }
        ),
        // Aktuelle Ansicht ausblenden
        animate$1(currentRef, { opacity: 0 }, { duration: ANIMATION_DURATION.MOBILE_FADE, easing: ANIMATION_EASING.OUT }),
        // Nächste Ansicht einblenden (mit kleiner Verzögerung für natürlichen Übergang)
        (async () => {
          await new Promise((resolve) => setTimeout(resolve, 50));
          return animate$1(nextRef, { opacity: 1 }, { duration: ANIMATION_DURATION.MOBILE_FADE, easing: ANIMATION_EASING.IN });
        })()
      ]);
      actionBarRef.value.style.height = "auto";
      await nextTick();
      if (internalMobileView.value) {
        if (mobileRef.value) {
          mobileRef.value.style.opacity = "";
          mobileRef.value.style.visibility = "";
          mobileRef.value.style.display = "";
        }
        if (initialRef.value) {
          initialRef.value.style.opacity = "0";
          initialRef.value.style.visibility = "hidden";
          initialRef.value.style.display = "none";
        }
      } else {
        if (initialRef.value) {
          initialRef.value.style.opacity = "";
          initialRef.value.style.visibility = "";
          initialRef.value.style.display = "";
        }
        if (mobileRef.value) {
          mobileRef.value.style.opacity = "0";
          mobileRef.value.style.visibility = "hidden";
          mobileRef.value.style.display = "none";
        }
      }
      updateBodyPadding();
      updateActionBarHeight();
    };
    __expose({
      expand,
      collapse,
      announce,
      scrollToTop,
      toggleMobile
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "actionBarRef",
        ref: actionBarRef,
        class: "action-bar theme-light",
        role: "region",
        "aria-label": _ctx.ariaLabel,
        "aria-expanded": internalExpanded.value
      }, [
        createElementVNode("div", {
          ref_key: "ariaLiveRef",
          ref: ariaLiveRef,
          class: "action-bar__sr-only",
          "aria-live": "polite",
          "aria-atomic": "true"
        }, toDisplayString(ariaLiveMessage.value), 513),
        withDirectives(createElementVNode("div", {
          ref_key: "initialRef",
          ref: initialRef,
          class: "action-bar__initial"
        }, [
          unref(isMobileBreakpoint) && hasMobileSlot.value ? (openBlock(), createBlock(_sfc_main$1, {
            key: 0,
            "style-type": "link",
            "icon-type": "icon-right",
            "icon-name": "arrow-down-bold",
            size: "small",
            class: "action-bar__mobile-toggle",
            onClick: toggleMobile
          }, {
            default: withCtx(() => [
              createElementVNode("span", null, toDisplayString(_ctx.closeButtonLabel), 1)
            ]),
            _: 1
          })) : createCommentVNode("", true),
          renderSlot(_ctx.$slots, "initial-content")
        ], 512), [
          [vShow, !internalExpanded.value && (!unref(isMobileBreakpoint) || !internalMobileView.value)]
        ]),
        withDirectives(createElementVNode("div", {
          ref_key: "mobileRef",
          ref: mobileRef,
          class: "action-bar__mobile"
        }, [
          renderSlot(_ctx.$slots, "mobile-content")
        ], 512), [
          [vShow, !internalExpanded.value && unref(isMobileBreakpoint) && internalMobileView.value]
        ]),
        withDirectives(createElementVNode("div", {
          ref_key: "expandedRef",
          ref: expandedRef,
          class: "action-bar__expanded"
        }, [
          createElementVNode("div", {
            ref_key: "expandedContentRef",
            ref: expandedContentRef,
            class: "action-bar__expanded-outer"
          }, [
            createElementVNode("div", _hoisted_2, [
              createElementVNode("div", _hoisted_3, [
                _ctx.expandedHeadline ? (openBlock(), createElementBlock("h3", _hoisted_4, [
                  _ctx.showCheckmark ? (openBlock(), createBlock(_sfc_main$2, {
                    key: 0,
                    name: "checkmark-circle-full",
                    class: "action-bar__header-checkmark"
                  })) : createCommentVNode("", true),
                  createTextVNode(" " + toDisplayString(_ctx.expandedHeadline), 1)
                ])) : createCommentVNode("", true),
                createElementVNode("button", {
                  ref_key: "closeButtonRef",
                  ref: closeButtonRef,
                  type: "button",
                  class: "action-bar__close-button",
                  onClick: handleClose
                }, [
                  createVNode(_sfc_main$2, { name: "close-thick" }),
                  createElementVNode("span", _hoisted_5, toDisplayString(_ctx.closeButtonLabel), 1)
                ], 512)
              ]),
              renderSlot(_ctx.$slots, "expanded-content")
            ])
          ], 512)
        ], 512), [
          [vShow, internalExpanded.value]
        ])
      ], 8, _hoisted_1);
    };
  }
});

export { _sfc_main as default };
